import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  ShieldIcon, BookIcon, TransportIcon, MessageSquareIcon,
  MealIcon, DocumentIcon, ReceiptIcon, SparkleIcon,
  ArrowRightIcon, CheckIcon, CloseIcon, ZapIcon,
} from "@/components/ui/TriIcons";
import { products, type ProductDetail } from "@/lib/product-data";

const EASE = [0.4, 0, 0.2, 1] as const;
const WINE = "#7B1416";
const WINE_L = "#A42022";
const SAND = "#C4A882";
const CHARCOAL = "#1C1917";
const CREAM = "#F5EFE4";
const CREAM2 = "#EDE4D4";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', 'Playfair Display', serif";
const FONT_SANS = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";

export function InteractiveDemo({
  productId,
  onClose,
}: {
  productId: string;
  onClose: () => void;
}) {
  const product = products.find((p) => p.id === productId);
  const [tab, setTab] = useState(0);

  if (!product) return null;

  const tabs = demoTabs[product.id] ?? ["Overview"];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(10,10,10,0.6)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        transition={{ ease: EASE, duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border bg-[#F5EFE4] shadow-2xl"
        style={{ borderColor: `${product.color}20` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: product.gradient }}>
              <product.icon className="h-5 w-5" accent="#fff" style={{ color: "#fff" }} />
            </div>
            <div>
              <h3 className="font-display text-lg font-medium" style={{ color: "#1C1917" }}>{product.name} — Interactive Demo</h3>
              <p className="text-xs text-[#888]">{product.tagline}</p>
            </div>
          </div>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-black/[0.06]">
            <CloseIcon className="h-5 w-5" accent="#888" style={{ color: "#888" }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b px-6 py-2" style={{ borderColor: `${product.color}10` }}>
          {tabs.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${tab === i ? "text-white" : "text-[#888] hover:text-[#1C1917]"}`}
              style={tab === i ? { background: product.color } : {}}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Demo content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${product.id}-${tab}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {product.id === "identity" && <IdentityDemo tab={tab} product={product} />}
              {product.id === "learn" && <LearnDemo tab={tab} product={product} />}
              {product.id === "transit" && <TransitDemo tab={tab} product={product} />}
              {product.id === "connect" && <ConnectDemo tab={tab} product={product} />}
              {product.id === "campus" && <CampusDemo tab={tab} product={product} />}
              {product.id === "intelligence" && <IntelligenceDemo tab={tab} product={product} />}
              {product.id === "government" && <GovernmentDemo tab={tab} product={product} />}
              {product.id === "pay" && <PayDemo tab={tab} product={product} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

const demoTabs: Record<string, string[]> = {
  identity: ["ID Card", "Biometric", "Permissions", "QR Scan"],
  learn: ["Gradebook", "AI Tutor", "Timetable", "Assignments"],
  transit: ["Live Map", "Route Planner", "Boarding Log", "Alerts"],
  connect: ["Message Stream", "Broadcast", "Translation", "Analytics"],
  campus: ["Living Campus", "Student Journey", "Cafeteria", "Boarding", "Security", "Intelligence"],
  intelligence: ["AI Query", "Predictions", "Risk Radar", "Reports"],
  government: ["Compliance", "EMIS Report", "Audit Trail", "Risk Score"],
  pay: ["Payment Flow", "Sub-Wallets", "Reconciliation", "Collections AI"],
};

// ════════════════════════════════════════════════════════════
// IDENTITY DEMO
// ════════════════════════════════════════════════════════════
export function IdentityDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [scanProgress, setScanProgress] = useState(0);
  const [scanState, setScanState] = useState<"idle" | "scanning" | "verified">("idle");
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [qrScanned, setQrScanned] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startScan = () => {
    setScanState("scanning");
    setScanProgress(0);
    intervalRef.current = setInterval(() => {
      setScanProgress((p) => {
        if (p >= 100) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setScanState("verified");
          return 100;
        }
        return p + 2;
      });
    }, 30);
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const roles = [
    { name: "Student", perms: ["Attendance", "Library", "Cafeteria", "Transport", "Exams"], color: product.color },
    { name: "Parent", perms: ["View Grades", "Pay Fees", "Message Teachers", "Track Bus"], color: "#C4A882" },
    { name: "Teacher", perms: ["Grade Book", "Attendance", "Messages", "Assignments"], color: "#8A6520" },
    { name: "Admin", perms: ["All Systems", "User Management", "Reports", "Finance"], color: "#E07B00" },
  ];

  if (tab === 0) {
    return (
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="flex flex-col items-center justify-center">
          <motion.div
            initial={{ rotateY: -15 }}
            animate={{ rotateY: scanState === "verified" ? [0, -3, 3, 0] : 0 }}
            transition={{ duration: 0.4 }}
            className="relative w-80 rounded-2xl p-6 shadow-2xl"
            style={{ background: product.gradient, aspectRatio: "1.586" }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-white/60">TriSphere ID</p>
                <p className="mt-1 font-display text-lg font-medium text-white">Student Identity</p>
              </div>
              <ShieldIcon className="h-8 w-8" accent="#fff" style={{ color: "#fff", opacity: 0.8 }} />
            </div>
            <div className="mt-8">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-white/20 text-white text-sm font-medium">SN</div>
              <p className="mt-3 text-sm font-medium text-white">Sarah Nakato</p>
              <p className="text-[10px] text-white/60">ID: TS-2024-08192</p>
              <p className="text-[10px] text-white/60">St. Mary's College Kampala</p>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded bg-white/15">
                <svg viewBox="0 0 20 20" className="h-5 w-5"><rect x="2" y="2" width="3" height="3" fill="#fff"/><rect x="8" y="2" width="3" height="3" fill="#fff"/><rect x="14" y="2" width="3" height="3" fill="#fff"/><rect x="2" y="8" width="3" height="3" fill="#fff"/><rect x="8" y="8" width="3" height="3" fill="#fff"/><rect x="14" y="8" width="3" height="3" fill="#fff"/><rect x="2" y="14" width="3" height="3" fill="#fff"/><rect x="8" y="14" width="3" height="3" fill="#fff"/></svg>
              </div>
              <span className="text-[8px] text-white/40">QR Code</span>
              <div className="ml-auto flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div key={i} className="h-1.5 w-1.5 rounded-full bg-white/40" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, delay: i * 0.3, repeat: Infinity }} />
                ))}
              </div>
            </div>
            {scanState === "verified" && (
              <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="absolute -right-3 -top-3 grid h-10 w-10 place-items-center rounded-full bg-green-500 shadow-lg">
                <CheckIcon className="h-5 w-5" accent="#fff" style={{ color: "#fff" }} />
              </motion.div>
            )}
          </motion.div>
          <button onClick={startScan} disabled={scanState === "scanning"} className="mt-6 rounded-full px-6 py-2.5 text-sm font-medium text-white transition disabled:opacity-50" style={{ background: product.color }}>
            {scanState === "idle" && "Verify Identity"}
            {scanState === "scanning" && "Scanning..."}
            {scanState === "verified" && "Verified — Scan Again"}
          </button>
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[#888]">Identity Types</p>
            <div className="grid grid-cols-2 gap-2">
              {["Student", "Parent", "Teacher", "Staff", "Alumni", "Visitor", "Institution", "Contractor"].map((t) => (
                <div key={t} className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "rgba(26,26,26,0.06)" }}>
                  <div className="h-2 w-2 rounded-full" style={{ background: product.color }} />
                  <span className="text-xs font-medium text-[#1C1917]">{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
            <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Identity Properties</p>
            <div className="mt-3 space-y-2">
              {[
                ["Persistent across schools", true],
                ["Biometric verification", true],
                ["QR + NFC credentials", true],
                ["Consent & privacy controls", true],
                ["Multi-school enrollment", true],
              ].map(([label, ok]) => (
                <div key={label as string} className="flex items-center gap-2">
                  <CheckIcon className="h-4 w-4" accent={product.color} style={{ color: product.color }} />
                  <span className="text-sm text-[#4A4A4A]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 1) {
    return (
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="flex flex-col items-center justify-center rounded-2xl border p-8" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <div className="relative h-48 w-48">
            <svg className="h-full w-full" viewBox="0 0 200 200">
              <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(26,26,26,0.06)" strokeWidth="3" />
              <motion.circle
                cx="100" cy="100" r="90" fill="none" stroke={product.color} strokeWidth="3" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 90}
                animate={{ strokeDashoffset: 2 * Math.PI * 90 * (1 - scanProgress / 100) }}
                transform="rotate(-90 100 100)"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <ShieldIcon className="h-12 w-12" accent={product.color} style={{ color: product.color }} />
              <p className="mt-2 font-display text-2xl font-bold" style={{ color: product.color }}>{scanProgress}%</p>
            </div>
          </div>
          <p className="mt-4 text-sm font-medium text-[#1C1917]">
            {scanState === "idle" && "Ready to scan"}
            {scanState === "scanning" && "Scanning biometric data..."}
            {scanState === "verified" && "Identity verified successfully"}
          </p>
          <button onClick={startScan} disabled={scanState === "scanning"} className="mt-4 rounded-full px-6 py-2.5 text-sm font-medium text-white transition disabled:opacity-50" style={{ background: product.color }}>
            {scanState === "scanning" ? "Scanning..." : "Start Biometric Scan"}
          </button>
        </div>
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Biometric Layers</p>
          {[
            { name: "Fingerprint", status: scanProgress > 20, delay: 0 },
            { name: "Facial Recognition", status: scanProgress > 50, delay: 1 },
            { name: "Iris Scan", status: scanProgress > 80, delay: 2 },
            { name: "Voice Pattern", status: scanProgress >= 100, delay: 3 },
          ].map((layer) => (
            <motion.div
              key={layer.name}
              animate={{ opacity: layer.status ? 1 : 0.4 }}
              className="flex items-center justify-between rounded-xl border p-4"
              style={{ borderColor: layer.status ? `${product.color}30` : "rgba(26,26,26,0.06)", background: layer.status ? `${product.color}06` : "#fff" }}
            >
              <span className="text-sm font-medium text-[#1C1917]">{layer.name}</span>
              {layer.status ? (
                <span className="flex items-center gap-1 text-xs font-medium" style={{ color: product.color }}>
                  <CheckIcon className="h-3.5 w-3.5" accent={product.color} style={{ color: product.color }} /> Verified
                </span>
              ) : (
                <span className="text-xs text-[#aaa]">Pending</span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (tab === 2) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Select a role to view permissions</p>
          <div className="space-y-2">
            {roles.map((r) => (
              <button
                key={r.name}
                onClick={() => setSelectedRole(r.name)}
                className="flex w-full items-center justify-between rounded-xl border p-4 text-left transition-all"
                style={{
                  borderColor: selectedRole === r.name ? `${r.color}40` : "rgba(26,26,26,0.06)",
                  background: selectedRole === r.name ? `${r.color}06` : "#fff",
                }}
              >
                <span className="text-sm font-medium text-[#1C1917]">{r.name}</span>
                <div className="h-2 w-2 rounded-full" style={{ background: r.color }} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Permissions</p>
          <div className="space-y-2">
            <AnimatePresence mode="wait">
              {selectedRole ? (
                <motion.div
                  key={selectedRole}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-2"
                >
                  {roles.find((r) => r.name === selectedRole)!.perms.map((perm) => (
                    <motion.div
                      key={perm}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2 rounded-xl border p-3"
                      style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}
                    >
                      <div className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: `${product.color}12` }}>
                        <CheckIcon className="h-3.5 w-3.5" accent={product.color} style={{ color: product.color }} />
                      </div>
                      <span className="text-sm font-medium text-[#1C1917]">{perm}</span>
                      <span className="ml-auto text-[10px] font-medium text-green-600">GRANTED</span>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-40 items-center justify-center rounded-2xl border-dashed border text-sm text-[#aaa]"
                >
                  Select a role to see permissions
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  }

  // QR Scan tab
  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8">
      <div className="relative h-56 w-56 overflow-hidden rounded-2xl border-2" style={{ borderColor: product.color, background: "#fff" }}>
        <div className="absolute inset-0 grid place-items-center">
          <svg viewBox="0 0 20 20" className="h-32 w-32"><rect x="2" y="2" width="3" height="3" fill="#1C1917"/><rect x="8" y="2" width="3" height="3" fill="#1C1917"/><rect x="14" y="2" width="3" height="3" fill="#1C1917"/><rect x="2" y="8" width="3" height="3" fill="#1C1917"/><rect x="8" y="8" width="3" height="3" fill="#1C1917"/><rect x="14" y="8" width="3" height="3" fill="#1C1917"/><rect x="2" y="14" width="3" height="3" fill="#1C1917"/><rect x="8" y="14" width="3" height="3" fill="#1C1917"/><rect x="14" y="14" width="3" height="3" fill="#1C1917"/></svg>
        </div>
        <motion.div
          className="absolute left-0 right-0 h-0.5"
          style={{ background: product.color, boxShadow: `0 0 12px ${product.color}` }}
          animate={qrScanned ? { top: "100%" } : { top: ["0%", "100%", "0%"] }}
          transition={qrScanned ? { duration: 0.2 } : { duration: 2, repeat: Infinity, ease: "linear" }}
        />
        {qrScanned && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-green-500">
            <CheckIcon className="h-4 w-4" accent="#fff" style={{ color: "#fff" }} />
          </motion.div>
        )}
      </div>
      <button onClick={() => { setQrScanned(true); setTimeout(() => setQrScanned(false), 2000); }} className="rounded-full px-6 py-2.5 text-sm font-medium text-white transition" style={{ background: product.color }}>
        {qrScanned ? "Scanned!" : "Simulate QR Scan"}
      </button>
      <div className="grid grid-cols-3 gap-4 text-center">
        {[
          { label: "Scan time", value: "0.3s" },
          { label: "Match rate", value: "99.9%" },
          { label: "Offline mode", value: "Yes" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
            <p className="font-display text-xl font-bold" style={{ color: product.color }}>{s.value}</p>
            <p className="mt-1 text-[10px] uppercase tracking-wider text-[#888]">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEARN DEMO
// ════════════════════════════════════════════════════════════
export function LearnDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [selectedSubj, setSelectedSubj] = useState(0);
  const [assignmentFilter, setAssignmentFilter] = useState("All");
  const [chatMessages, setChatMessages] = useState<{ role: "ai" | "user"; text: string }[]>([
    { role: "ai", text: "Hi! I'm your AI Study Assistant. I noticed you scored 92% in Science. Would you like personalized recommendations?" },
  ]);
  const [input, setInput] = useState("");
  const chatRef = useRef<HTMLDivElement>(null);

  const subjects = [
    { name: "Mathematics", grade: "A-", score: 87, trend: "+5%", color: product.color },
    { name: "English", grade: "B+", score: 78, trend: "+3%", color: "#C4A882" },
    { name: "Science", grade: "A", score: 92, trend: "+8%", color: "#8A6520" },
    { name: "History", grade: "B", score: 75, trend: "+2%", color: "#E07B00" },
    { name: "Geography", grade: "A-", score: 85, trend: "+4%", color: "#7B1FA2" },
    { name: "Chemistry", grade: "B+", score: 80, trend: "+6%", color: "#007AFF" },
  ];

  const assignments = [
    { subject: "Mathematics", title: "Algebra Worksheet 12", due: "Tomorrow", status: "Pending" },
    { subject: "Science", title: "Lab Report: Photosynthesis", due: "Jul 28", status: "Submitted" },
    { subject: "English", title: "Essay: African Literature", due: "Aug 2", status: "Pending" },
    { subject: "History", title: "Chapter 5 Q&A", due: "Jul 30", status: "Graded" },
    { subject: "Geography", title: "Map Assignment", due: "Aug 5", status: "Pending" },
    { subject: "Chemistry", title: "Periodic Table Quiz", due: "Jul 25", status: "Submitted" },
  ];

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setChatMessages((m) => [...m, { role: "user", text: userMsg }]);
    setInput("");
    setTimeout(() => {
      const responses: Record<string, string> = {
        help: "I can help with Mathematics, Science, English, and more. Which subject would you like to focus on?",
        math: "For Mathematics, I recommend practicing algebraic identities and quadratic equations. I've assigned 3 practice problems.",
        science: "Great choice! Based on your 92% score, I suggest exploring advanced topics in cellular biology. I've curated 5 resources.",
        exam: "Based on your current grades, you're on track for a B+ average. Focus on History and English to push to an A average.",
      };
      const key = Object.keys(responses).find((k) => userMsg.toLowerCase().includes(k)) ?? "default";
      const response = responses[key] ?? `I understand you're asking about "${userMsg}". Based on your learning profile, I recommend reviewing the materials I've flagged in your dashboard. Would you like me to create a study plan?`;
      setChatMessages((m) => [...m, { role: "ai", text: response }]);
    }, 600);
  };

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [chatMessages]);

  if (tab === 0) {
    return (
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Grade Book · Term 2 · Sarah Nakato</p>
          <div className="space-y-2">
            {subjects.map((s, i) => (
              <button
                key={s.name}
                onClick={() => setSelectedSubj(i)}
                className="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all"
                style={{
                  borderColor: selectedSubj === i ? `${s.color}40` : "rgba(26,26,26,0.06)",
                  background: selectedSubj === i ? `${s.color}06` : "#fff",
                }}
              >
                <span className="w-24 text-sm font-medium text-[#1C1917]">{s.name}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
                  <motion.div className="h-full rounded-full" style={{ background: s.color }} animate={{ width: `${s.score}%` }} transition={{ delay: i * 0.05 }} />
                </div>
                <span className="w-8 text-right text-sm font-bold" style={{ color: s.color }}>{s.grade}</span>
                <span className="w-12 text-right text-[10px] text-[#888]">{s.trend}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Subject Detail</p>
          <AnimatePresence mode="wait">
            <motion.div key={selectedSubj} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <p className="mt-3 font-display text-2xl font-medium" style={{ color: subjects[selectedSubj].color }}>{subjects[selectedSubj].name}</p>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-5xl font-bold" style={{ color: subjects[selectedSubj].color }}>{subjects[selectedSubj].score}%</span>
                <span className="mb-1 text-sm text-[#888]">Grade: {subjects[selectedSubj].grade}</span>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-[#888]">Class average</span><span className="font-medium text-[#1C1917]">{Math.round(subjects[selectedSubj].score * 0.85)}%</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#888]">Rank</span><span className="font-medium text-[#1C1917]">3rd of 42</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#888]">Trend</span><span className="font-medium text-green-600">{subjects[selectedSubj].trend}</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#888]">Teacher</span><span className="font-medium text-[#1C1917]">Mr. Okello</span></div>
              </div>
              <div className="mt-4 rounded-xl bg-white p-3">
                <p className="text-xs text-[#888]">Teacher's Note</p>
                <p className="mt-1 text-sm text-[#4A4A4A]">Excellent progress this term. Sarah shows strong analytical skills. Recommend advanced coursework next term.</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  if (tab === 1) {
    return (
      <div className="flex flex-col" style={{ height: 460 }}>
        <div ref={chatRef} className="flex-1 space-y-3 overflow-y-auto rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          {chatMessages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={msg.role === "user" ? "ml-auto w-fit max-w-[80%] rounded-xl rounded-tr-sm p-3 text-sm text-white" : "w-fit max-w-[80%] rounded-xl rounded-tl-sm p-3 text-sm"}
              style={msg.role === "user" ? { background: product.color } : { background: `${product.color}08`, color: "#4A4A4A" }}
            >
              {msg.text}
            </motion.div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Ask about grades, recommendations, study plans..."
            className="flex-1 rounded-full border px-4 py-2.5 text-sm outline-none"
            style={{ borderColor: `${product.color}20` }}
          />
          <button onClick={sendMessage} className="grid h-10 w-10 place-items-center rounded-full text-white transition" style={{ background: product.color }}>
            <ArrowRightIcon className="h-4 w-4" accent="#fff" style={{ color: "#fff" }} />
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {["help", "math", "science", "exam"].map((s) => (
            <button key={s} onClick={() => { setInput(s); }} className="rounded-full border px-3 py-1 text-xs capitalize transition hover:shadow-sm" style={{ borderColor: `${product.color}20`, color: product.color }}>
              "{s}"
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (tab === 2) {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri"];
    const periods = [
      [{ s: "Mathematics", t: "Mr. Okello", c: product.color }, { s: "English", t: "Ms. Nakato", c: "#C4A882" }, { s: "Science", t: "Mr. Ssebunya", c: "#8A6520" }, { s: "History", t: "Mrs. Auma", c: "#E07B00" }, { s: "Geography", t: "Mr. Lubega", c: "#7B1FA2" }],
      [{ s: "Science", t: "Mr. Ssebunya", c: "#8A6520" }, { s: "Mathematics", t: "Mr. Okello", c: product.color }, { s: "Chemistry", t: "Ms. Apio", c: "#007AFF" }, { s: "English", t: "Ms. Nakato", c: "#C4A882" }, { s: "Geography", t: "Mr. Lubega", c: "#7B1FA2" }],
      [{ s: "History", t: "Mrs. Auma", c: "#E07B00" }, { s: "Science", t: "Mr. Ssebunya", c: "#8A6520" }, { s: "Mathematics", t: "Mr. Okello", c: product.color }, { s: "Chemistry", t: "Ms. Apio", c: "#007AFF" }, { s: "English", t: "Ms. Nakato", c: "#C4A882" }],
      [{ s: "Geography", t: "Mr. Lubega", c: "#7B1FA2" }, { s: "Mathematics", t: "Mr. Okello", c: product.color }, { s: "Science", t: "Mr. Ssebunya", c: "#8A6520" }, { s: "History", t: "Mrs. Auma", c: "#E07B00" }, { s: "Chemistry", t: "Ms. Apio", c: "#007AFF" }],
      [{ s: "English", t: "Ms. Nakato", c: "#C4A882" }, { s: "Chemistry", t: "Ms. Apio", c: "#007AFF" }, { s: "Mathematics", t: "Mr. Okello", c: product.color }, { s: "Geography", t: "Mr. Lubega", c: "#7B1FA2" }, { s: "Science", t: "Mr. Ssebunya", c: "#8A6520" }],
    ];
    return (
      <div className="overflow-x-auto">
        <div className="grid gap-2" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
          <div />
          {days.map((d) => <div key={d} className="text-center text-xs font-medium uppercase tracking-wider text-[#888]">{d}</div>)}
          {periods.map((day, di) => (
            <>
              <div key={`time-${di}`} className="flex items-center text-xs text-[#888]">{`9:${di * 2 + 8}:00`}</div>
              {day.map((cls, ci) => (
                <motion.div
                  key={`${di}-${ci}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: (di * 5 + ci) * 0.02 }}
                  className="rounded-xl border p-3"
                  style={{ borderColor: `${cls.c}30`, background: `${cls.c}08` }}
                >
                  <p className="text-xs font-medium" style={{ color: cls.c }}>{cls.s}</p>
                  <p className="mt-0.5 text-[10px] text-[#888]">{cls.t}</p>
                </motion.div>
              ))}
            </>
          ))}
        </div>
      </div>
    );
  }

  // Assignments tab
  const filtered = assignmentFilter === "All" ? assignments : assignments.filter((a) => a.status === assignmentFilter);
  return (
    <div>
      <div className="mb-4 flex gap-2">
        {["All", "Pending", "Submitted", "Graded"].map((f) => (
          <button key={f} onClick={() => setAssignmentFilter(f)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${assignmentFilter === f ? "text-white" : "text-[#888] hover:text-[#1C1917]"}`} style={assignmentFilter === f ? { background: product.color } : {}}>
            {f}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {filtered.map((a, i) => (
            <motion.div
              key={a.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center justify-between rounded-xl border p-4"
              style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}
            >
              <div>
                <p className="text-sm font-medium text-[#1C1917]">{a.title}</p>
                <p className="text-xs text-[#888]">{a.subject}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#888]">Due: {a.due}</span>
                <span className="rounded-full px-3 py-1 text-[10px] font-medium text-white" style={{ background: a.status === "Pending" ? "#E07B00" : a.status === "Submitted" ? "#007AFF" : "#8A6520" }}>{a.status}</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TRANSIT DEMO — real Google Maps tracking platform
// ════════════════════════════════════════════════════════════
import { LiveBusMap } from "@/components/live-bus-map";
import { SCHOOLS, BUSES } from "@/lib/transit-data";

export function TransitDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [alertText, setAlertText] = useState<string | null>(null);
  const [boardingLog, setBoardingLog] = useState<{ name: string; action: string; time: string }[]>([
    { name: "Sarah Nakato", action: "Boarded Bus 12", time: "7:42 AM" },
    { name: "John Mukasa", action: "Boarded Bus 12", time: "7:45 AM" },
    { name: "Grace Auma", action: "Boarded Bus 12", time: "7:48 AM" },
  ]);

  const triggerAlert = (type: string) => {
    const alerts: Record<string, string> = {
      breakdown: "ALERT: Bus 07 engine temperature critical. Dispatching replacement vehicle.",
      delay: "NOTIFICATION: Bus 12 will arrive 5 minutes late due to traffic on Jinja Road.",
      emergency: "EMERGENCY: Bus 23 emergency button pressed. GPS location shared with school admin and parents.",
      geofence: "GEOFENCE: Student boarded Bus 12 at Ntinda stop. Parent notified automatically.",
    };
    setAlertText(alerts[type]);
    setTimeout(() => setAlertText(null), 4000);
  };

  if (tab === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[#4A4A4A]">
          Live fleet tracking on real Google Maps. {SCHOOLS.length} schools, {BUSES.length} buses — each assigned to one school, following real road routes through Kampala neighborhoods with traffic-aware routing, live telemetry, student pickup simulation, and school arrival tracking.
        </p>
        <LiveBusMap
          height={520}
          autoTick
          wialon
          canViewTrails
          canViewHistory
          title="TriSphere Transit — Live Fleet Tracking"
        />
        <AnimatePresence>
          {alertText && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700">
              {alertText}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (tab === 1) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Route Optimization</p>
          <div className="space-y-3">
            {[
              { route: "Kampala–Ntinda", stops: 8, distance: "12 km", time: "35 min", optimized: true },
              { route: "Kira–Najjera", stops: 6, distance: "8 km", time: "28 min", optimized: true },
              { route: "Entebbe–Kajjansi", stops: 10, distance: "18 km", time: "45 min", optimized: false },
            ].map((r) => (
              <div key={r.route} className="rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1C1917]">{r.route}</span>
                  {r.optimized && <span className="rounded-full bg-green-50 px-2 py-0.5 text-[9px] font-medium text-green-600">OPTIMIZED</span>}
                </div>
                <div className="mt-2 flex gap-4 text-xs text-[#888]">
                  <span>{r.stops} stops</span>
                  <span>{r.distance}</span>
                  <span>{r.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">AI Route Suggestions</p>
          <div className="space-y-3">
            <div className="rounded-xl bg-white p-4">
              <p className="text-sm font-medium text-[#1C1917]">Merge Routes 7 & 12</p>
              <p className="mt-1 text-xs text-[#4A4A4A]">AI detected 68% overlap between Bus 07 and Bus 12 routes. Merging saves 22% fuel and 15 min/day.</p>
              <button className="mt-3 rounded-full px-4 py-1.5 text-xs font-medium text-white" style={{ background: product.color }}>Apply Optimization</button>
            </div>
            <div className="rounded-xl bg-white p-4">
              <p className="text-sm font-medium text-[#1C1917]">Add Stop: Acacia Avenue</p>
              <p className="mt-1 text-xs text-[#4A4A4A]">3 students within 500m of this location. Adding a stop reduces their walk by 12 minutes.</p>
              <button className="mt-3 rounded-full px-4 py-1.5 text-xs font-medium text-white" style={{ background: product.color }}>Add Stop</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 2) {
    return (
      <div>
        <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Student Boarding Log · Bus 12 · Today</p>
        <div className="space-y-2">
          {boardingLog.map((log, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
              <div className="grid h-8 w-8 place-items-center rounded-full" style={{ background: `${product.color}12` }}>
                <CheckIcon className="h-4 w-4" accent={product.color} style={{ color: product.color }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1C1917]">{log.name}</p>
                <p className="text-xs text-[#888]">{log.action}</p>
              </div>
              <span className="text-xs text-[#888]">{log.time}</span>
            </motion.div>
          ))}
        </div>
        <button onClick={() => {
          const names = ["David Ssali", "Ruth Akello", "Peter Otieno", "Mary Atim"];
          const name = names[Math.floor(Math.random() * names.length)];
          const now = new Date();
          const time = `${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")} AM`;
          setBoardingLog((prev) => [...prev, { name, action: "Boarded Bus 12", time }]);
        }} className="mt-4 rounded-full px-5 py-2 text-sm font-medium text-white" style={{ background: product.color }}>
          Simulate Boarding
        </button>
      </div>
    );
  }

  // Alerts tab
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {[
        { type: "breakdown", title: "Vehicle Breakdown", desc: "Engine fault detected — auto-dispatch replacement", color: "#D32F2F" },
        { type: "delay", title: "Route Delay", desc: "Traffic congestion — notify parents with new ETA", color: "#E07B00" },
        { type: "emergency", title: "Emergency Button", desc: "Driver pressed SOS — share GPS with admin & parents", color: "#D32F2F" },
        { type: "geofence", title: "Geofence Entry", desc: "Student boarded bus — auto-notify parent", color: product.color },
      ].map((a) => (
        <button key={a.type} onClick={() => triggerAlert(a.type)} className="rounded-2xl border p-5 text-left transition hover:shadow-md" style={{ borderColor: `${a.color}20`, background: "#fff" }}>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full" style={{ background: a.color }} />
            <span className="text-sm font-medium text-[#1C1917]">{a.title}</span>
          </div>
          <p className="mt-2 text-xs text-[#4A4A4A]">{a.desc}</p>
        </button>
      ))}
      <AnimatePresence>
        {alertText && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="sm:col-span-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
            {alertText}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CONNECT DEMO
// ════════════════════════════════════════════════════════════
export function ConnectDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [messages, setMessages] = useState([
    { channel: "WhatsApp", from: "Teacher Nakato", text: "Sarah scored 92% on her Science test today!", time: "2:30 PM", color: "#25D366", read: true },
    { channel: "SMS", from: "School Admin", text: "PTA meeting rescheduled to Friday 3 PM", time: "1:15 PM", color: "#007AFF", read: true },
    { channel: "App", from: "Transport", text: "Bus 12 has arrived at Ntinda stop", time: "12:42 PM", color: product.color, read: false },
    { channel: "Email", from: "Bursar", text: "Term 2 fees invoice generated — UGX 850,000", time: "11:00 AM", color: "#E07B00", read: true },
  ]);
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastChannel, setBroadcastChannel] = useState("All Channels");
  const [sentCount, setSentCount] = useState(0);
  const [translateText, setTranslateText] = useState("Your child scored 92% on the Science test today.");
  const [targetLang, setTargetLang] = useState("Luganda");
  const [translated, setTranslated] = useState("");

  const translations: Record<string, string> = {
    Luganda: "Omwana wo yafunye 92% ku kaweereza ka Sayansi olwaleero.",
    Swahili: "Mtoto wako alipata 92% katika mtihani wa Sayansi leo.",
    French: "Votre enfant a obtenu 92% à l'examen de Sciences aujourd'hui.",
    Runyankole: "Omwana wo yatwire 92% mu kihandiiko kya Sayansi erizo.",
  };

  const sendBroadcast = () => {
    if (!broadcastText.trim()) return;
    setSentCount(0);
    const total = 1247;
    const id = setInterval(() => {
      setSentCount((c) => {
        if (c >= total) { clearInterval(id); return total; }
        return Math.min(total, c + 50);
      });
    }, 30);
    setTimeout(() => {
      setMessages((prev) => [{
        channel: broadcastChannel === "All Channels" ? "Broadcast" : broadcastChannel,
        from: "School Admin",
        text: broadcastText,
        time: "Just now",
        color: product.color,
        read: false,
      }, ...prev]);
      setBroadcastText("");
      setTimeout(() => setSentCount(0), 3000);
    }, 2000);
  };

  if (tab === 0) {
    return (
      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Unified Channels</p>
          <div className="grid grid-cols-2 gap-2">
            {["WhatsApp", "SMS", "Email", "Push", "Voice", "In-App"].map((ch, i) => (
              <motion.div key={ch} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }} className="flex items-center gap-2 rounded-xl border p-3" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
                <motion.div className="h-2 w-2 rounded-full" style={{ background: product.color }} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }} />
                <span className="text-xs font-medium text-[#1C1917]">{ch}</span>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border p-4" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#888]">Delivery rate</span>
              <span className="font-display text-2xl font-medium" style={{ color: product.color }}>99.2%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
              <motion.div className="h-full rounded-full" style={{ background: product.color }} initial={{ width: 0 }} animate={{ width: "99.2%" }} transition={{ duration: 0.8 }} />
            </div>
          </div>
        </div>
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Unified Message Stream</p>
          <div className="space-y-2">
            {messages.map((msg, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: msg.read ? "#fff" : `${product.color}04` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-medium text-white" style={{ background: msg.color }}>{msg.channel}</span>
                    <span className="text-xs font-medium text-[#1C1917]">{msg.from}</span>
                    {!msg.read && <span className="h-2 w-2 rounded-full" style={{ background: product.color }} />}
                  </div>
                  <span className="text-[10px] text-[#aaa]">{msg.time}</span>
                </div>
                <p className="mt-2 text-sm text-[#4A4A4A]">{msg.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (tab === 1) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Broadcast Announcement</p>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <textarea value={broadcastText} onChange={(e) => setBroadcastText(e.target.value)} placeholder="Type your announcement..." className="w-full resize-none rounded-xl border p-3 text-sm outline-none" style={{ borderColor: `${product.color}20` }} rows={3} />
          <div className="mt-3 flex items-center justify-between">
            <select value={broadcastChannel} onChange={(e) => setBroadcastChannel(e.target.value)} className="rounded-full border px-4 py-2 text-sm outline-none" style={{ borderColor: `${product.color}20` }}>
              <option>All Channels</option>
              <option>WhatsApp</option>
              <option>SMS</option>
              <option>Email</option>
              <option>Push</option>
            </select>
            <button onClick={sendBroadcast} disabled={!broadcastText.trim() || sentCount > 0} className="rounded-full px-6 py-2 text-sm font-medium text-white transition disabled:opacity-50" style={{ background: product.color }}>
              {sentCount > 0 ? `Sent to ${sentCount.toLocaleString()}` : "Send Broadcast"}
            </button>
          </div>
          {sentCount > 0 && (
            <div className="mt-3">
              <div className="h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                <motion.div className="h-full rounded-full" style={{ background: product.color }} animate={{ width: `${(sentCount / 1247) * 100}%` }} transition={{ duration: 0.05 }} />
              </div>
              <p className="mt-1 text-xs text-[#888]">Delivering to 1,247 recipients across {broadcastChannel}...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (tab === 2) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Auto-Translation</p>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <p className="mb-2 text-xs text-[#888]">Original (English)</p>
          <textarea value={translateText} onChange={(e) => setTranslateText(e.target.value)} className="w-full resize-none rounded-xl border p-3 text-sm outline-none" style={{ borderColor: `${product.color}20` }} rows={2} />
          <div className="mt-3 flex gap-2">
            {Object.keys(translations).map((lang) => (
              <button key={lang} onClick={() => { setTargetLang(lang); setTranslated(translations[lang]); }} className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${targetLang === lang ? "text-white" : "text-[#888]"}`} style={targetLang === lang ? { background: product.color } : { borderColor: `${product.color}20`, border: "1px solid" }}>
                {lang}
              </button>
            ))}
          </div>
          {translated && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl p-4" style={{ background: `${product.color}06` }}>
              <p className="mb-1 text-xs text-[#888]">Translated ({targetLang})</p>
              <p className="text-sm font-medium text-[#1C1917]">{translated}</p>
            </motion.div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          {[
            { label: "Languages", value: "12" },
            { label: "Accuracy", value: "96%" },
            { label: "Latency", value: "0.5s" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
              <p className="font-display text-xl font-bold" style={{ color: product.color }}>{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-[#888]">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Analytics tab
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
        <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Engagement by Channel</p>
        <div className="space-y-3">
          {[
            { ch: "WhatsApp", pct: 78, color: "#25D366" },
            { ch: "SMS", pct: 92, color: "#007AFF" },
            { ch: "Email", pct: 45, color: "#E07B00" },
            { ch: "Push", pct: 68, color: product.color },
            { ch: "In-App", pct: 85, color: "#7B1FA2" },
          ].map((c) => (
            <div key={c.ch}>
              <div className="mb-1 flex justify-between text-xs"><span className="font-medium text-[#1C1917]">{c.ch}</span><span className="text-[#888]">{c.pct}%</span></div>
              <div className="h-2 overflow-hidden rounded-full bg-black/[0.06]">
                <motion.div className="h-full rounded-full" style={{ background: c.color }} initial={{ width: 0 }} animate={{ width: `${c.pct}%` }} transition={{ duration: 0.6 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
        <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Communication Stats · This Month</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Messages sent", value: "2.4M" },
            { label: "Delivery rate", value: "99.2%" },
            { label: "Read rate", value: "87%" },
            { label: "Response time", value: "4.2 min" },
            { label: "Active parents", value: "38K" },
            { label: "Broadcasts", value: "142" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-white p-4">
              <p className="font-display text-2xl font-bold" style={{ color: product.color }}>{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-[#888]">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CAMPUS DEMO
// ═════════════════════════════ mealItems.map((m) => m.name) ═════════════════════════════
export function CampusDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [cart, setCart] = useState<string[]>([]);
  const [allergyAlert, setAllergyAlert] = useState<string | null>(null);
  const [visitorName, setVisitorName] = useState("");
  const [visitorPurpose, setVisitorPurpose] = useState("Parent Visit");
  const [visitorLog, setVisitorLog] = useState([
    { name: "Robert Ssali", purpose: "Parent-Teacher Conference", time: "10:30 AM", status: "Checked In", id: "CF-8472" },
    { name: "Linda Nakato", purpose: "Delivery — Books", time: "11:15 AM", status: "Checked In", id: "CF-8473" },
    { name: "James Okello", purpose: "Maintenance — Plumbing", time: "9:00 AM", status: "Checked Out", id: "CF-8470" },
    { name: "Grace Atim", purpose: "Parent Pickup — Sarah Nakato", time: "3:45 PM", status: "Pending", id: "CF-8475" },
  ]);
  const [dormTab, setDormTab] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [hoveredStat, setHoveredStat] = useState<number | null>(null);

  // ── Tab 0: Living Campus Dashboard ──
  if (tab === 0) {
    const liveStats = [
      { label: "Students on campus", value: 2481, max: 2800, icon: "👥", color: product.color, sub: "of 2,800 enrolled" },
      { label: "Staff present", value: 173, max: 195, icon: "🧑‍🏫", color: "#8A6520", sub: "of 195 total" },
      { label: "Attendance rate", value: 96, max: 100, icon: "✓", color: "#007AFF", sub: "Form 1–6 combined", suffix: "%" },
      { label: "Buses active", value: 18, max: 22, icon: "🚌", color: "#E07B00", sub: "4 in maintenance" },
      { label: "Meals served today", value: 742, max: 1200, icon: "🍽", color: "#7B1FA2", sub: "Breakfast + Lunch" },
      { label: "Clinic visits", value: 31, max: 50, icon: "⚕", color: "#D32F2F", sub: "3 awaiting referral" },
      { label: "Library transactions", value: 412, max: 600, icon: "📚", color: "#8A6520", sub: "Loans + returns" },
      { label: "Visitors on site", value: 54, max: 80, icon: "🪪", color: "#E07B00", sub: "12 pending pickup" },
      { label: "Maintenance jobs", value: 6, max: 15, icon: "🔧", color: "#888", sub: "2 urgent · 4 routine" },
      { label: "Sports activities", value: 18, max: 25, icon: "⚽", color: "#007AFF", sub: "Football, Netball, Athletics" },
      { label: "Parent pickups", value: 12, max: 30, icon: "🚗", color: "#7B1FA2", sub: "Scheduled for today" },
      { label: "Boarding inspections", value: 4, max: 8, icon: "🛏", color: "#D32F2F", sub: "2 pending · 2 complete" },
    ];
    const zones = [
      { name: "Academic Block", occupancy: 87, status: "active" },
      { name: "Dormitory A — Boys", occupancy: 94, status: "active" },
      { name: "Dormitory B — Girls", occupancy: 91, status: "active" },
      { name: "Cafeteria", occupancy: 65, status: "serving" },
      { name: "Library", occupancy: 72, status: "active" },
      { name: "Sports Complex", occupancy: 58, status: "active" },
      { name: "Health Centre", occupancy: 40, status: "active" },
      { name: "Admin Block", occupancy: 95, status: "active" },
    ];
    return (
      <div>
        <div className="mb-4 flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Living Campus · Real-time operational awareness</p>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
            <span className="text-[10px] font-medium text-green-600">LIVE</span>
            <span className="text-[10px] text-[#aaa]">· Updated 2s ago</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {liveStats.map((s, i) => {
            const pct = Math.round((s.value / s.max) * 100);
            const isHovered = hoveredStat === i;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onMouseEnter={() => setHoveredStat(i)}
                onMouseLeave={() => setHoveredStat(null)}
                className="group relative overflow-hidden rounded-2xl border p-4 transition-all"
                style={{ borderColor: isHovered ? `${s.color}40` : "rgba(26,26,26,0.06)", background: isHovered ? `${s.color}04` : "#fff", boxShadow: isHovered ? `0 4px 20px ${s.color}15` : "none" }}
              >
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xl">{s.icon}</span>
                  <span className="text-[9px] font-medium uppercase tracking-wider" style={{ color: s.color }}>{s.suffix ?? ""}</span>
                </div>
                <div className="font-display text-2xl font-bold tabular-nums" style={{ color: s.color }}>{s.value.toLocaleString()}{s.suffix ?? ""}</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-[#888]">{s.label}</div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-black/[0.06]">
                  <motion.div className="h-full rounded-full" style={{ background: s.color }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: i * 0.03 + 0.2, duration: 0.5 }} />
                </div>
                <p className="mt-1 text-[9px] text-[#aaa]">{s.sub}</p>
              </motion.div>
            );
          })}
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-4 rounded-2xl border p-4" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-[#888]">Zone Occupancy · Live</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {zones.map((z, i) => (
              <motion.div key={z.name} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 + i * 0.04 }} className="rounded-xl border p-2.5" style={{ borderColor: "rgba(26,26,26,0.04)", background: `${product.color}03` }}>
                <div className="text-[10px] font-medium text-[#4A4A4A]">{z.name}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
                    <motion.div className="h-full rounded-full" style={{ background: z.occupancy > 90 ? "#D32F2F" : z.occupancy > 70 ? product.color : "#A42022" }} initial={{ width: 0 }} animate={{ width: `${z.occupancy}%` }} transition={{ delay: 0.4 + i * 0.04 + 0.2, duration: 0.4 }} />
                  </div>
                  <span className="text-[10px] font-medium tabular-nums" style={{ color: z.occupancy > 90 ? "#D32F2F" : "#4A4A4A" }}>{z.occupancy}%</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Tab 1: Smart Student Journey ──
  if (tab === 1) {
    const journey = [
      { time: "06:05 AM", event: "Boards Bus 12", detail: "GPS confirms boarding at Naalya Stop 3 · Parent notified via app", icon: "🚌", category: "transport" },
      { time: "07:14 AM", event: "Bus enters campus", detail: "Geofence triggered · Attendance automatically confirmed", icon: "🏫", category: "arrival" },
      { time: "07:16 AM", event: "Enters Gate 3 via NFC ID", detail: "Security logs arrival · Temperature scan normal (36.4°C)", icon: "🪪", category: "security" },
      { time: "07:22 AM", event: "Breakfast purchased", detail: "Wallet deducted UGX 2,000 · Nutrition recorded: 420 kcal", icon: "🍽", category: "cafeteria" },
      { time: "08:00 AM", event: "Class attendance confirmed", detail: "Form 3B · 42/44 present · 2 excused absence", icon: "✓", category: "academic" },
      { time: "10:45 AM", event: "Library visit", detail: "Borrowed: 'Chemistry for Senior Secondary' · Due in 14 days", icon: "📚", category: "library" },
      { time: "12:40 PM", event: "Lunch purchase blocked", detail: "AI blocked groundnut sauce (peanut allergy) · Safe alternative: Rice & Beef Stew suggested", icon: "⚠", category: "cafeteria", alert: true },
      { time: "02:15 PM", event: "Clinic visit", detail: "Temperature: 37.8°C · Paracetamol administered · Parents notified via SMS + app", icon: "⚕", category: "health" },
      { time: "03:30 PM", event: "Football practice", detail: "Attended 90-min session · Heart rate avg: 142 bpm · No injuries", icon: "⚽", category: "sports" },
      { time: "04:05 PM", event: "Boards return bus", detail: "GPS confirms departure · Parent receives ETA: 4:35 PM", icon: "🚗", category: "transport" },
    ];
    const categoryColors: Record<string, string> = {
      transport: "#E07B00", arrival: product.color, security: "#D32F2F", cafeteria: "#7B1FA2",
      academic: "#007AFF", library: "#A42022", health: "#D32F2F", sports: "#007AFF",
    };
    return (
      <div>
        <div className="mb-4 flex items-center gap-3 rounded-2xl border p-3" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <div className="grid h-12 w-12 place-items-center rounded-full text-lg font-bold text-white" style={{ background: product.color }}>SN</div>
          <div>
            <p className="text-sm font-bold text-[#1C1917]">Sarah Nakato</p>
            <p className="text-xs text-[#888]">Form 3B · Student #STU-2024-0847 · House: Mandela</p>
          </div>
          <div className="ml-auto flex gap-1.5">
            <span className="rounded-full px-2.5 py-1 text-[10px] font-medium" style={{ background: `${product.color}12`, color: product.color }}>Present</span>
            <span className="rounded-full bg-purple-50 px-2.5 py-1 text-[10px] font-medium text-purple-700">Boarder</span>
            <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-medium text-orange-700">Allergies: Peanuts, Dairy</span>
          </div>
        </div>
        <div className="relative">
          <div className="absolute left-[23px] top-3 bottom-3 w-0.5" style={{ background: `linear-gradient(to bottom, ${product.color}30, ${product.color}08)` }} />
          <div className="space-y-2.5">
            {journey.map((step, i) => {
              const catColor = categoryColors[step.category] ?? product.color;
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className="relative flex items-start gap-4">
                  <div className="z-10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-lg shadow-sm" style={{ background: step.alert ? "#FFF3E0" : `${catColor}12`, border: `2px solid ${step.alert ? "#E07B00" : `${catColor}30`}` }}>
                    {step.icon}
                  </div>
                  <div className="flex-1 rounded-xl border p-3 transition hover:shadow-md" style={{ borderColor: step.alert ? "#E07B0030" : "rgba(26,26,26,0.06)", background: step.alert ? "#FFF8E5" : "#fff" }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#1C1917]">{step.event}</span>
                        <span className="rounded-full px-2 py-0.5 text-[8px] font-medium uppercase tracking-wider" style={{ background: `${catColor}10`, color: catColor }}>{step.category}</span>
                      </div>
                      <span className="font-mono text-xs text-[#aaa]">{step.time}</span>
                    </div>
                    <p className="mt-1 text-xs text-[#888]">{step.detail}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Tab 2: Intelligent Cafeteria ──
  const mealItems = [
    { name: "Posho & Beans", price: 3000, allergens: [] as string[], kcal: 450, category: "Main" },
    { name: "Groundnut Sauce", price: 2000, allergens: ["Peanuts"], kcal: 380, category: "Sauce" },
    { name: "Rice & Beef Stew", price: 5000, allergens: [] as string[], kcal: 620, category: "Main" },
    { name: "Chapati & Tea", price: 1500, allergens: ["Gluten"], kcal: 280, category: "Snack" },
    { name: "Fresh Fruit Cup", price: 2000, allergens: [] as string[], kcal: 150, category: "Healthy" },
    { name: "Samosa (2 pcs)", price: 2500, allergens: ["Gluten", "Dairy"] as string[], kcal: 340, category: "Snack" },
    { name: "Vegetable Soup", price: 2000, allergens: [] as string[], kcal: 180, category: "Healthy" },
    { name: "Matooke & Groundnuts", price: 3500, allergens: ["Peanuts"], kcal: 520, category: "Main" },
  ];
  const studentAllergies = ["Peanuts", "Dairy"];
  const addToCart = (item: { name: string; allergens: string[]; price: number }) => {
    const conflict = item.allergens.filter((a) => studentAllergies.includes(a));
    if (conflict.length > 0) {
      setAllergyAlert(`BLOCKED: ${item.name} contains ${conflict.join(", ")} — Sarah has ${conflict.join(", ")} allergy. Suggested safe alternative: Fresh Fruit Cup or Vegetable Soup.`);
      setTimeout(() => setAllergyAlert(null), 4500);
      return;
    }
    setCart((c) => [...c, item.name]);
  };
  const total = cart.reduce((sum, name) => sum + (mealItems.find((m) => m.name === name)?.price ?? 0), 0);
  const totalKcal = cart.reduce((sum, name) => sum + (mealItems.find((m) => m.name === name)?.kcal ?? 0), 0);

  if (tab === 2) {
    const safetyChecks = [
      { label: "Identity verified", passed: true },
      { label: "Meal entitlement", passed: true },
      { label: "Wallet balance", passed: true, value: "UGX 12,500" },
      { label: "Boarding schedule", passed: true },
      { label: "Food allergies", passed: true, value: "Peanuts, Dairy" },
      { label: "Medical restrictions", passed: true },
      { label: "Daily nutrition goals", passed: true, value: "1,800 kcal" },
      { label: "Parent restrictions", passed: true },
      { label: "Duplicate purchase", passed: true },
      { label: "Meal plan valid", passed: true },
    ];
    return (
      <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Intelligent Cafeteria · Sarah Nakato</p>
            <span className="rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-medium text-green-700">All 10 safety checks passed</span>
          </div>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {safetyChecks.map((c) => (
              <span key={c.label} className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-medium" style={{ background: `${product.color}08`, color: product.color }}>
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />{c.label}{c.value ? `: ${c.value}` : ""}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {mealItems.map((item) => {
              const conflict = item.allergens.filter((a) => studentAllergies.includes(a));
              const blocked = conflict.length > 0;
              return (
                <button key={item.name} onClick={() => addToCart(item)} disabled={blocked} className="group relative overflow-hidden rounded-xl border p-3 text-left transition-all disabled:cursor-not-allowed" style={{ borderColor: blocked ? "rgba(211,47,47,0.15)" : "rgba(26,26,26,0.06)", background: blocked ? "rgba(211,47,47,0.02)" : "#fff", opacity: blocked ? 0.55 : 1 }}>
                  {blocked && <div className="absolute right-2 top-2 rounded-full bg-red-100 px-2 py-0.5 text-[8px] font-bold text-red-600">BLOCKED</div>}
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[#1C1917]">{item.name}</p>
                    <span className="rounded-full bg-black/[0.04] px-1.5 py-0.5 text-[8px] text-[#888]">{item.category}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-xs font-medium" style={{ color: product.color }}>UGX {item.price.toLocaleString()}</p>
                    <p className="text-[10px] text-[#aaa]">{item.kcal} kcal</p>
                  </div>
                  {item.allergens.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {item.allergens.map((a) => (
                        <span key={a} className="rounded-full px-1.5 py-0.5 text-[8px] font-medium" style={{ background: studentAllergies.includes(a) ? "#D32F2F" : "#888", color: "#fff" }}>{a}</span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <AnimatePresence>
            {allergyAlert && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700">
                <span className="text-base">⚠</span>
                <span>{allergyAlert}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex flex-col rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[#888]">Current Order</p>
          <div className="flex-1 space-y-2">
            <AnimatePresence mode="popLayout">
              {cart.map((item, i) => (
                <motion.div key={`${item}-${i}`} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="flex items-center justify-between rounded-xl bg-white p-3 shadow-sm">
                  <span className="text-sm font-medium text-[#1C1917]">{item}</span>
                  <span className="text-xs" style={{ color: product.color }}>UGX {mealItems.find((m) => m.name === item)?.price.toLocaleString()}</span>
                </motion.div>
              ))}
            </AnimatePresence>
            {cart.length === 0 && <p className="py-8 text-center text-sm text-[#aaa]">No items in cart</p>}
          </div>
          <div className="mt-4 space-y-2 border-t pt-4" style={{ borderColor: `${product.color}15` }}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#888]">Items</span>
              <span className="font-medium text-[#1C1917]">{cart.length}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#888]">Calories</span>
              <span className="font-medium text-[#1C1917]">{totalKcal} kcal</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#888]">Wallet balance</span>
              <span className="font-medium text-[#1C1917]">UGX 12,500</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#1C1917]">Total</span>
              <span className="font-display text-xl font-bold" style={{ color: product.color }}>UGX {total.toLocaleString()}</span>
            </div>
            <button onClick={() => { if (cart.length > 0) setCart([]); }} disabled={cart.length === 0} className="w-full rounded-full py-2.5 text-sm font-medium text-white transition disabled:opacity-50" style={{ background: product.color }}>
              {cart.length > 0 ? "Pay with Wallet" : "Add items to cart"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Tab 3: Boarding Intelligence ──
  if (tab === 3) {
    const dorms = [
      { name: "Mandela House — Boys", beds: 120, occupied: 114, rollCall: "Complete", inspections: 3, issues: 2 },
      { name: "Rwenzori House — Girls", beds: 110, occupied: 103, rollCall: "Complete", inspections: 4, issues: 1 },
      { name: "Victoria House — Boys", beds: 90, occupied: 85, rollCall: "In Progress", inspections: 2, issues: 0 },
      { name: "Ssese House — Girls", beds: 85, occupied: 82, rollCall: "Complete", inspections: 3, issues: 3 },
    ];
    const boardingTasks = [
      { label: "Morning Roll Call", time: "5:30 AM", status: "Complete", icon: "🌅" },
      { label: "Breakfast Queue", time: "6:00 AM", status: "Complete", icon: "🍽" },
      { label: "Study Hall Attendance", time: "7:00 PM", status: "Pending", icon: "📖" },
      { label: "Night Roll Call", time: "9:30 PM", status: "Pending", icon: "🌙" },
      { label: "Lights-Out Compliance", time: "10:00 PM", status: "Pending", icon: "💡" },
      { label: "Weekend Leave Processing", time: "Fri 3:00 PM", status: "Pending", icon: "📋" },
    ];
    return (
      <div className="space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Boarding Intelligence · Purpose-built for Ugandan boarding schools</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {dorms.map((d, i) => {
            const pct = Math.round((d.occupied / d.beds) * 100);
            return (
              <motion.div key={d.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-2xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
                <p className="text-sm font-semibold text-[#1C1917]">{d.name}</p>
                <div className="mt-2 flex items-end gap-1">
                  <span className="font-display text-2xl font-bold" style={{ color: product.color }}>{d.occupied}</span>
                  <span className="mb-0.5 text-xs text-[#888]">/ {d.beds} beds</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                  <motion.div className="h-full rounded-full" style={{ background: pct > 95 ? "#D32F2F" : product.color }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: i * 0.05 + 0.2, duration: 0.4 }} />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-medium ${d.rollCall === "Complete" ? "bg-green-50 text-green-700" : "bg-orange-50 text-orange-700"}`}>{d.rollCall}</span>
                  <span className="text-[10px] text-[#888]">{d.inspections} inspections · {d.issues} issues</span>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-[#888]">Daily Boarding Operations</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {boardingTasks.map((t, i) => (
              <motion.div key={t.label} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }} className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: "rgba(26,26,26,0.04)", background: "#fff" }}>
                <span className="text-lg">{t.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1C1917]">{t.label}</p>
                  <p className="text-[10px] text-[#888]">{t.time}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-medium ${t.status === "Complete" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{t.status}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Tab 4: Security Operations ──
  if (tab === 4) {
    const secStats = [
      { label: "Gates monitored", value: "6", icon: "🚪" },
      { label: "Vehicles logged", value: "128", icon: "🚙" },
      { label: "QR badges active", value: "54", icon: "📱" },
      { label: "Patrols active", value: "3", icon: "🛡" },
      { label: "Cameras online", value: "42", icon: "📹" },
      { label: "Alerts today", value: "2", icon: "⚠" },
    ];
    return (
      <div className="space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Security Operations · Every gate, visitor, vehicle and movement</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {secStats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }} className="rounded-xl border p-3 text-center" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
              <div className="mb-1 text-lg">{s.icon}</div>
              <div className="font-display text-xl font-bold" style={{ color: product.color }}>{s.value}</div>
              <div className="mt-0.5 text-[9px] uppercase tracking-wider text-[#888]">{s.label}</div>
            </motion.div>
          ))}
        </div>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[#888]">Check In New Visitor</p>
          <div className="flex flex-wrap gap-2">
            <input value={visitorName} onChange={(e) => setVisitorName(e.target.value)} placeholder="Visitor name or National ID..." className="flex-1 rounded-full border px-4 py-2.5 text-sm outline-none" style={{ borderColor: `${product.color}20` }} />
            <select value={visitorPurpose} onChange={(e) => setVisitorPurpose(e.target.value)} className="rounded-full border px-4 py-2.5 text-sm outline-none" style={{ borderColor: `${product.color}20` }}>
              <option>Parent Visit</option>
              <option>Delivery</option>
              <option>Maintenance</option>
              <option>Parent Pickup</option>
              <option>Contractor</option>
              <option>Official Visit</option>
            </select>
            <button onClick={() => {
              if (!visitorName.trim()) return;
              const now = new Date();
              const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
              const id = `CF-${Math.floor(8000 + Math.random() * 999)}`;
              setVisitorLog((prev) => [{ name: visitorName, purpose: visitorPurpose, time, status: "Checked In", id }, ...prev]);
              setVisitorName("");
            }} className="rounded-full px-5 py-2.5 text-sm font-medium text-white" style={{ background: product.color }}>Check In & Issue QR Badge</button>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#888]">Visitor Log · Today</p>
          {visitorLog.map((v, i) => (
            <motion.div key={v.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} className="flex items-center justify-between rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-full text-xs font-bold" style={{ background: `${product.color}10`, color: product.color }}>{v.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
                <div>
                  <p className="text-sm font-medium text-[#1C1917]">{v.name}</p>
                  <p className="text-xs text-[#888]">{v.purpose} · {v.time} · Badge: {v.id}</p>
                </div>
              </div>
              <span className="rounded-full px-3 py-1 text-[10px] font-medium" style={{ background: v.status === "Checked In" ? `${product.color}12` : v.status === "Pending" ? "#FFF3E0" : "rgba(26,26,26,0.04)", color: v.status === "Checked In" ? product.color : v.status === "Pending" ? "#E07B00" : "#888" }}>{v.status}</span>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // ── Tab 5: Campus Intelligence ──
  const predictions = [
    { label: "Cafeteria demand (lunch)", prediction: "1,180 meals", confidence: 92, icon: "🍽", detail: "Forecast based on boarding count, day of week, and weather" },
    { label: "Bus loading (morning)", prediction: "94% capacity", confidence: 88, icon: "🚌", detail: "Routes 7 & 12 may exceed capacity — consider adding a vehicle" },
    { label: "Clinic staffing need", prediction: "2 nurses", confidence: 85, icon: "⚕", detail: "Seasonal flu peak expected in 2 weeks — increase clinic staff" },
    { label: "Library demand", prediction: "620 visits", confidence: 79, icon: "📚", detail: "Exam preparation week — extend library hours to 10 PM" },
    { label: "Equipment replacement", prediction: "3 projectors", confidence: 94, icon: "🔧", detail: "Based on maintenance history — projectors in Labs 2, 4, 6 at end-of-life" },
    { label: "Dormitory occupancy (holiday)", prediction: "12 students", confidence: 96, icon: "🛏", detail: "Holiday break — 12 students approved for stay-back" },
    { label: "Attendance anomaly", prediction: "3 flagged", confidence: 90, icon: "⚠", detail: "Students with sudden attendance drop — possible safeguarding concern" },
    { label: "Maintenance backlog", prediction: "8 work orders", confidence: 87, icon: "📋", detail: "Preventive maintenance due next week — 5 plumbing, 3 electrical" },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 animate-pulse rounded-full" style={{ background: product.color }} />
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Campus Intelligence · Predictive operational insights</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {predictions.map((p, i) => (
          <motion.div key={p.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="group rounded-2xl border p-4 transition hover:shadow-md" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
            <div className="flex items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-xl" style={{ background: `${product.color}08` }}>{p.icon}</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#1C1917]">{p.label}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-display text-lg font-bold" style={{ color: product.color }}>{p.prediction}</span>
                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-[9px] font-medium text-green-700">{p.confidence}% confidence</span>
                </div>
                <p className="mt-1 text-xs text-[#888]">{p.detail}</p>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-black/[0.06]">
                  <motion.div className="h-full rounded-full" style={{ background: product.color }} initial={{ width: 0 }} animate={{ width: `${p.confidence}%` }} transition={{ delay: i * 0.05 + 0.2, duration: 0.5 }} />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="rounded-2xl border p-4" style={{ borderColor: `${product.color}20`, background: `${product.color}06` }}>
        <p className="text-xs text-[#4A4A4A]">School leaders move from reacting to problems to preventing them. TriSphere continuously learns how the school operates and predicts demand, staffing needs, equipment failures, and safeguarding concerns before they occur.</p>
      </motion.div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// INTELLIGENCE DEMO
// ════════════════════════════════════════════════════════════
export function IntelligenceDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [radarData] = useState(() => Array.from({ length: 8 }, () => ({ angle: Math.random() * 360, intensity: Math.random() * 0.8 + 0.2 })));

  const askAI = () => {
    if (!query.trim()) return;
    setLoading(true);
    setAnswer(null);
    setTimeout(() => {
      const responses: Record<string, string> = {
        dropout: "23 students flagged for dropout risk. Top factors: attendance decline (62%), fee delay (45%), grade drop (38%). Recommended: schedule counseling sessions for top 10 at-risk students.",
        routes: "Route analysis complete. Bus 07 and Bus 12 have 68% route overlap. Merging saves UGX 1.2M/year in fuel. Bus 23 is operating at 94% capacity — consider adding a vehicle.",
        fees: "UGX 45M in outstanding fees across 312 students. 68% are 1-2 terms behind. AI predicts 85% collection rate if payment plans are offered. Top 15 accounts need personal follow-up.",
        performance: "School performance is above national average by 12%. However, Mathematics scores declined 5% this term. Recommend additional teacher training and 3 hours/week of remedial classes.",
      };
      const key = Object.keys(responses).find((k) => query.toLowerCase().includes(k)) ?? "default";
      setAnswer(responses[key] ?? `Based on real-time data analysis across all connected systems, I've identified 3 key insights: 1) Attendance is trending +3% this week, 2) Fee collection is at 91% (above target), 3) 23 students need academic intervention. Would you like a detailed breakdown?`);
      setLoading(false);
    }, 1200);
  };

  if (tab === 0) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Ask TriSphere AI anything about your institution</p>
        <div className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAI()} placeholder="e.g. 'Which students are at risk of dropping out?'" className="flex-1 rounded-full border px-5 py-3 text-sm outline-none" style={{ borderColor: `${product.color}20` }} />
          <button onClick={askAI} disabled={loading || !query.trim()} className="grid h-12 w-12 place-items-center rounded-full text-white transition disabled:opacity-50" style={{ background: product.color }}>
            {loading ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white" /> : <ArrowRightIcon className="h-5 w-5" accent="#fff" style={{ color: "#fff" }} />}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {["Which students at risk of dropping out?", "Which routes are inefficient?", "Which fees are at risk?", "How is school performance?"].map((s) => (
            <button key={s} onClick={() => setQuery(s)} className="rounded-full border px-3 py-1.5 text-xs text-[#888] transition hover:shadow-sm" style={{ borderColor: `${product.color}20` }}>{s}</button>
          ))}
        </div>
        <AnimatePresence>
          {answer && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border p-5" style={{ borderColor: `${product.color}20`, background: `${product.color}06` }}>
              <div className="flex items-start gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${product.color}15` }}>
                  <SparkleIcon className="h-4 w-4" accent={product.color} style={{ color: product.color }} />
                </div>
                <p className="text-sm leading-relaxed text-[#1C1917]">{answer}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (tab === 1) {
    const predictions = [
      { label: "Dropout risk", value: 23, color: "#E07B00", trend: "+5%" },
      { label: "Fee collection", value: 91, color: product.color, trend: "+3%" },
      { label: "Attendance", value: 96, color: "#8A6520", trend: "+2%" },
      { label: "Academic performance", value: 87, color: product.color, trend: "-1%" },
      { label: "Transport efficiency", value: 89, color: "#007AFF", trend: "+8%" },
      { label: "Parent engagement", value: 78, color: "#7B1FA2", trend: "+4%" },
    ];
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {predictions.map((p, i) => (
          <motion.div key={p.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }} className="rounded-2xl border p-5" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
            <p className="text-xs font-medium uppercase tracking-wider text-[#888]">{p.label}</p>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-display text-4xl font-bold" style={{ color: p.color }}>{p.value}%</span>
              <span className="mb-1 text-xs font-medium" style={{ color: p.trend.startsWith("+") ? "#A42022" : "#D32F2F" }}>{p.trend}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-black/[0.06]">
              <motion.div className="h-full rounded-full" style={{ background: p.color }} initial={{ width: 0 }} animate={{ width: `${p.value}%` }} transition={{ delay: i * 0.06 + 0.2, duration: 0.6 }} />
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  if (tab === 2) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-4">
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Risk Radar · Real-time</p>
        <div className="relative h-64 w-64">
          <svg className="h-full w-full" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(26,26,26,0.06)" strokeWidth="1" />
            <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(26,26,26,0.06)" strokeWidth="1" />
            <circle cx="100" cy="100" r="30" fill="none" stroke="rgba(26,26,26,0.06)" strokeWidth="1" />
            <line x1="10" y1="100" x2="190" y2="100" stroke="rgba(26,26,26,0.04)" strokeWidth="1" />
            <line x1="100" y1="10" x2="100" y2="190" stroke="rgba(26,26,26,0.04)" strokeWidth="1" />
            <motion.path
              d={`M 100 100 L ${100 + 90 * Math.cos(radarData[0].angle * Math.PI / 180)} ${100 + 90 * Math.sin(radarData[0].angle * Math.PI / 180)} L ${100 + 90 * Math.cos(radarData[1].angle * Math.PI / 180)} ${100 + 90 * Math.sin(radarData[1].angle * Math.PI / 180)} Z`}
              fill={`${product.color}15`}
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              style={{ transformOrigin: "100px 100px" }}
            />
            {radarData.map((d, i) => {
              const x = 100 + 70 * Math.cos(d.angle * Math.PI / 180) * d.intensity;
              const y = 100 + 70 * Math.sin(d.angle * Math.PI / 180) * d.intensity;
              return <motion.circle key={i} cx={x} cy={y} r="4" fill={d.intensity > 0.6 ? "#D32F2F" : product.color} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }} />;
            })}
          </svg>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "High Risk", value: 3, color: "#D32F2F" },
            { label: "Medium Risk", value: 8, color: "#E07B00" },
            { label: "Low Risk", value: 23, color: product.color },
            { label: "Safe", value: 187, color: "#8A6520" },
          ].map((r) => (
            <div key={r.label} className="rounded-xl border p-3 text-center" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
              <p className="font-display text-2xl font-bold" style={{ color: r.color }}>{r.value}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#888]">{r.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Reports tab
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {[
        { title: "Student Success Report", desc: "Predictive analysis of 1,247 students", icon: "📊", color: product.color },
        { title: "Financial Health Report", desc: "Revenue, collections, and forecasting", icon: "💰", color: "#C4A882" },
        { title: "Transport Efficiency Report", desc: "Route optimization and fuel analysis", icon: "🚌", color: "#8A6520" },
        { title: "Academic Performance Report", desc: "Grade trends and curriculum alignment", icon: "📚", color: "#007AFF" },
      ].map((r, i) => (
        <motion.button key={r.title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="flex items-center gap-4 rounded-2xl border p-5 text-left transition hover:shadow-md" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
          <div className="grid h-12 w-12 place-items-center rounded-xl text-2xl" style={{ background: `${r.color}12` }}>{r.icon}</div>
          <div>
            <p className="text-sm font-medium text-[#1C1917]">{r.title}</p>
            <p className="mt-0.5 text-xs text-[#888]">{r.desc}</p>
          </div>
          <ArrowRightIcon className="ml-auto h-4 w-4 text-[#ccc]" accent="#ccc" style={{ color: "#ccc" }} />
        </motion.button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// GOVERNMENT DEMO
// ════════════════════════════════════════════════════════════
export function GovernmentDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const reports = [
    { name: "EMIS Quarterly", status: "Submitted", date: "Auto-generated Jul 15", color: "#8A6520" },
    { name: "Teacher Registry", status: "Synced", date: "Updated Jul 20", color: "#8A6520" },
    { name: "Financial Report", status: "Pending review", date: "Due Jul 31", color: "#E07B00" },
    { name: "Safeguarding Audit", status: "Compliant", date: "Passed Jun 28", color: "#8A6520" },
    { name: "Student Enrollment Census", status: "Submitted", date: "Auto-generated Jul 1", color: "#8A6520" },
    { name: "Curriculum Compliance", status: "Compliant", date: "Verified Jul 12", color: "#8A6520" },
  ];

  const auditTrail = [
    { action: "Report submitted: EMIS Quarterly", user: "System (Auto)", time: "Jul 15, 2:00 AM", type: "auto" },
    { action: "Teacher registry updated", user: "Admin: Mr. Ssali", time: "Jul 20, 9:15 AM", type: "manual" },
    { action: "Compliance check passed: Safeguarding", user: "System (Auto)", time: "Jun 28, 12:00 PM", type: "auto" },
    { action: "Policy updated: Data Privacy v2.3", user: "Ministry", time: "Jun 15, 3:30 PM", type: "policy" },
    { action: "Inspection scheduled: Aug 5", user: "Admin: Mrs. Auma", time: "Jul 18, 10:00 AM", type: "manual" },
  ];

  if (tab === 0) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Automated Regulatory Reports</p>
          <div className="space-y-2">
            {reports.map((r, i) => (
              <motion.div key={r.name} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="flex items-center justify-between rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
                <div>
                  <p className="text-sm font-medium text-[#1C1917]">{r.name}</p>
                  <p className="text-[10px] text-[#888]">{r.date}</p>
                </div>
                <span className="rounded-full px-3 py-1 text-[10px] font-medium text-white" style={{ background: r.color }}>{r.status}</span>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
            <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Compliance Score</p>
            <div className="mt-3 flex items-center gap-4">
              <div className="relative h-20 w-20">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(26,26,26,0.06)" strokeWidth="6" />
                  <motion.circle cx="50" cy="50" r="42" fill="none" stroke={product.color} strokeWidth="6" strokeLinecap="round" strokeDasharray={2 * Math.PI * 42} initial={{ strokeDashoffset: 2 * Math.PI * 42 }} animate={{ strokeDashoffset: 0 }} transition={{ duration: 1 }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center font-display text-xl font-bold" style={{ color: product.color }}>100%</div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#4A4A4A]">All regulatory requirements met across 5 jurisdictions</p>
                <p className="mt-1 text-xs text-[#888]">Zero manual reports needed this term</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
            <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Jurisdictions</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {["Ministry of Education", "UBTEB", "NCDC", "DCI", "KCCA"].map((j) => (
                <span key={j} className="rounded-full border px-3 py-1.5 text-xs font-medium" style={{ borderColor: `${product.color}20`, color: "#4A4A4A" }}>{j}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 1) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl" style={{ background: `${product.color}12` }}>
            <DocumentIcon className="h-8 w-8" accent={product.color} style={{ color: product.color }} />
          </div>
          <p className="mt-4 font-display text-xl font-medium text-[#1C1917]">EMIS Quarterly Report Generator</p>
          <p className="mt-2 text-sm text-[#4A4A4A]">Auto-compiles enrollment, attendance, staffing, and performance data from all connected systems.</p>
          <button
            onClick={() => { setGenerating(true); setGenerated(false); setTimeout(() => { setGenerating(false); setGenerated(true); }, 2000); }}
            disabled={generating}
            className="mt-6 rounded-full px-8 py-3 text-sm font-medium text-white transition disabled:opacity-50"
            style={{ background: product.color }}
          >
            {generating ? "Generating..." : generated ? "Regenerate Report" : "Generate EMIS Report"}
          </button>
          {generating && (
            <div className="mt-4 space-y-2 text-left">
              {["Collecting enrollment data...", "Compiling attendance records...", "Aggregating staffing info...", "Formatting for Ministry submission..."].map((step, i) => (
                <motion.div key={step} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.4 }} className="flex items-center gap-2 text-xs text-[#888]">
                  <CheckIcon className="h-3.5 w-3.5" accent="#8A6520" style={{ color: "#8A6520" }} /> {step}
                </motion.div>
              ))}
            </div>
          )}
          {generated && !generating && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-medium text-green-700">Report generated successfully!</p>
              <p className="mt-1 text-xs text-[#4A4A4A]">47 pages · 1,247 students · 89 teachers · 12 schools · Ready for Ministry submission</p>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  if (tab === 2) {
    return (
      <div>
        <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Audit Trail · All Actions Logged</p>
        <div className="space-y-2">
          {auditTrail.map((a, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="flex items-start gap-3 rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
              <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full" style={{ background: a.type === "auto" ? product.color : a.type === "policy" ? "#7B1FA2" : "#7B1416" }} />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1C1917]">{a.action}</p>
                <p className="text-xs text-[#888]">{a.user} · {a.time}</p>
              </div>
              <span className="rounded-full px-2 py-0.5 text-[9px] font-medium uppercase" style={{ background: a.type === "auto" ? `${product.color}12` : "rgba(26,26,26,0.04)", color: a.type === "auto" ? product.color : "#888" }}>{a.type}</span>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Risk Score tab
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
        <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Risk Assessment</p>
        <div className="mt-4 flex items-center gap-4">
          <div className="relative h-24 w-24">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(26,26,26,0.06)" strokeWidth="6" />
              <motion.circle cx="50" cy="50" r="42" fill="none" stroke="#8A6520" strokeWidth="6" strokeLinecap="round" strokeDasharray={2 * Math.PI * 42} initial={{ strokeDashoffset: 2 * Math.PI * 42 }} animate={{ strokeDashoffset: 2 * Math.PI * 42 * 0.92 }} transition={{ duration: 0.8 }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-2xl font-bold text-green-600">8%</span>
              <span className="text-[8px] uppercase text-[#888]">Risk</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-600">Low Risk</p>
            <p className="mt-1 text-xs text-[#4A4A4A]">No compliance risks detected. All systems operating within regulatory parameters.</p>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
        <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[#888]">Risk Factors</p>
        <div className="space-y-3">
          {[
            { factor: "Data Privacy", level: "Compliant", pct: 100, color: "#8A6520" },
            { factor: "Safeguarding", level: "Compliant", pct: 100, color: "#8A6520" },
            { factor: "Financial Records", level: "Minor gap", pct: 85, color: "#E07B00" },
            { factor: "Teacher Certification", level: "Compliant", pct: 100, color: "#8A6520" },
            { factor: "Curriculum Alignment", level: "Compliant", pct: 98, color: "#8A6520" },
          ].map((r) => (
            <div key={r.factor}>
              <div className="mb-1 flex justify-between text-xs"><span className="font-medium text-[#1C1917]">{r.factor}</span><span style={{ color: r.color }}>{r.level}</span></div>
              <div className="h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                <motion.div className="h-full rounded-full" style={{ background: r.color }} initial={{ width: 0 }} animate={{ width: `${r.pct}%` }} transition={{ duration: 0.6 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PAY DEMO
// ════════════════════════════════════════════════════════════
export function PayDemo({ tab, product }: { tab: number; product: ProductDetail }) {
  const [paymentStep, setPaymentStep] = useState(0);
  const [selectedRail, setSelectedRail] = useState<string | null>(null);
  const [reconciled, setReconciled] = useState(0);
  const [walletTransfer, setWalletTransfer] = useState({ from: "Tuition", to: "Meals", amount: "" });

  const rails = [
    { name: "MTN MoMo", color: "#FFCC00", text: "#000", fee: "1.5%" },
    { name: "Airtel Money", color: "#E40000", text: "#fff", fee: "1.5%" },
    { name: "Bank Transfer", color: "#007AFF", text: "#fff", fee: "UGX 2,000" },
    { name: "Visa Card", color: "#1A1F71", text: "#fff", fee: "2.5%" },
    { name: "Mastercard", color: "#EB001B", text: "#fff", fee: "2.5%" },
    { name: "Payroll Deduct", color: product.color, text: "#fff", fee: "0%" },
  ];

  const wallets = [
    { name: "Tuition", balance: 450000, color: product.color },
    { name: "Meals", balance: 12500, color: "#8A6520" },
    { name: "Transport", balance: 8000, color: "#E07B00" },
    { name: "Books", balance: 25000, color: "#7B1FA2" },
  ];

  if (tab === 0) {
    const steps = ["Select Amount", "Choose Payment Method", "Processing", "Confirmed"];
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <div className="flex items-center justify-between">
          {steps.map((s, i) => (
            <div key={s} className="flex flex-1 flex-col items-center">
              <div className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold transition ${paymentStep >= i ? "text-white" : "text-[#888]"}`} style={{ background: paymentStep >= i ? product.color : "rgba(26,26,26,0.06)" }}>
                {paymentStep > i ? <CheckIcon className="h-4 w-4" accent="#fff" style={{ color: "#fff" }} /> : i + 1}
              </div>
              <span className="mt-1 text-[9px] text-[#888]">{s}</span>
            </div>
          ))}
        </div>
        <AnimatePresence mode="wait">
          {paymentStep === 0 && (
            <motion.div key="step0" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="rounded-2xl border p-6" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
              <p className="text-sm text-[#888]">Paying: Term 2 Tuition — Sarah Nakato</p>
              <p className="mt-2 font-display text-4xl font-bold" style={{ color: product.color }}>UGX 850,000</p>
              <button onClick={() => setPaymentStep(1)} className="mt-6 w-full rounded-full py-3 text-sm font-medium text-white" style={{ background: product.color }}>Continue</button>
            </motion.div>
          )}
          {paymentStep === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="rounded-2xl border p-6" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
              <p className="mb-4 text-sm text-[#888]">Select payment method — AI will route to lowest cost</p>
              <div className="grid grid-cols-2 gap-2">
                {rails.map((r) => (
                  <button key={r.name} onClick={() => setSelectedRail(r.name)} className="flex items-center justify-between rounded-xl p-3 text-left transition" style={{ background: selectedRail === r.name ? `${product.color}06` : "#fff", border: `1px solid ${selectedRail === r.name ? `${product.color}40` : "rgba(26,26,26,0.06)"}` }}>
                    <span className="text-xs font-medium text-[#1C1917]">{r.name}</span>
                    <span className="text-[9px] text-[#888]">{r.fee}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setPaymentStep(2)} disabled={!selectedRail} className="mt-4 w-full rounded-full py-3 text-sm font-medium text-white transition disabled:opacity-50" style={{ background: product.color }}>Pay with {selectedRail ?? "..."}</button>
            </motion.div>
          )}
          {paymentStep === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="flex flex-col items-center rounded-2xl border p-8" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="h-12 w-12 rounded-full border-4 border-black/[0.06] border-t-current" style={{ borderTopColor: product.color }} />
              <p className="mt-4 text-sm font-medium text-[#1C1917]">Processing payment via {selectedRail}...</p>
              <p className="mt-1 text-xs text-[#888]">AI routing to lowest-cost rail</p>
            </motion.div>
          )}
          {paymentStep === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center rounded-2xl border p-8 text-center" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }} className="grid h-16 w-16 place-items-center rounded-full bg-green-500">
                <CheckIcon className="h-8 w-8" accent="#fff" style={{ color: "#fff" }} />
              </motion.div>
              <p className="mt-4 font-display text-xl font-medium text-[#1C1917]">Payment Successful</p>
              <p className="mt-1 text-sm text-[#4A4A4A]">UGX 850,000 paid via {selectedRail}</p>
              <p className="mt-1 text-xs text-[#888]">Fee: UGX 12,750 · Settlement: Instant</p>
              <button onClick={() => { setPaymentStep(0); setSelectedRail(null); }} className="mt-6 rounded-full border px-6 py-2 text-sm font-medium" style={{ borderColor: `${product.color}20`, color: product.color }}>New Payment</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (tab === 1) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Sub-Wallets · Restricted Funds</p>
          <div className="space-y-2">
            {wallets.map((w, i) => (
              <motion.div key={w.name} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} className="rounded-xl border p-4" style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1C1917]">{w.name}</span>
                  <span className="font-display text-sm font-bold" style={{ color: w.color }}>UGX {w.balance.toLocaleString()}</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                  <motion.div className="h-full rounded-full" style={{ background: w.color }} initial={{ width: 0 }} animate={{ width: `${(w.balance / 500000) * 100}%` }} transition={{ delay: i * 0.08 + 0.2 }} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">Transfer Between Wallets</p>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <select value={walletTransfer.from} onChange={(e) => setWalletTransfer((p) => ({ ...p, from: e.target.value }))} className="flex-1 rounded-xl border px-3 py-2.5 text-sm outline-none" style={{ borderColor: `${product.color}20` }}>
                {wallets.map((w) => <option key={w.name}>{w.name}</option>)}
              </select>
              <ArrowRightIcon className="h-4 w-4 text-[#888]" accent="#888" style={{ color: "#888" }} />
              <select value={walletTransfer.to} onChange={(e) => setWalletTransfer((p) => ({ ...p, to: e.target.value }))} className="flex-1 rounded-xl border px-3 py-2.5 text-sm outline-none" style={{ borderColor: `${product.color}20` }}>
                {wallets.map((w) => <option key={w.name}>{w.name}</option>)}
              </select>
            </div>
            <input value={walletTransfer.amount} onChange={(e) => setWalletTransfer((p) => ({ ...p, amount: e.target.value }))} placeholder="Amount (UGX)" className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none" style={{ borderColor: `${product.color}20` }} />
            <button
              onClick={() => { if (walletTransfer.amount) { setWalletTransfer({ from: "Tuition", to: "Meals", amount: "" }); } }}
              disabled={!walletTransfer.amount || walletTransfer.from === walletTransfer.to}
              className="w-full rounded-full py-2.5 text-sm font-medium text-white transition disabled:opacity-50"
              style={{ background: product.color }}
            >
              Transfer Funds
            </button>
            <p className="text-xs text-[#888]">Funds are restricted — can only be used for the designated purpose.</p>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 2) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border p-6" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-medium uppercase tracking-wider text-[#888]">Real-time Reconciliation</p>
            <button onClick={() => {
              setReconciled(0);
              const id = setInterval(() => {
                setReconciled((c) => { if (c >= 847) { clearInterval(id); return 847; } return c + 20; });
              }, 20);
            }} className="rounded-full px-4 py-1.5 text-xs font-medium text-white" style={{ background: product.color }}>Run Reconciliation</button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Transactions", value: 847, display: reconciled },
              { label: "Matched", value: 846, display: Math.floor(reconciled * 0.999) },
              { label: "Discrepancies", value: 1, display: Math.max(0, Math.floor(reconciled * 0.001)) },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border p-4 text-center" style={{ borderColor: "rgba(26,26,26,0.06)" }}>
                <p className="font-display text-2xl font-bold" style={{ color: product.color }}>{s.display.toLocaleString()}</p>
                <p className="mt-1 text-[10px] uppercase tracking-wider text-[#888]">{s.label}</p>
              </div>
            ))}
          </div>
          {reconciled > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 rounded-xl p-3" style={{ background: `${product.color}06` }}>
              <p className="text-sm text-[#4A4A4A]">846 of 847 transactions matched automatically. 1 discrepancy flagged for manual review (UGX 500 short-payment).</p>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // Collections AI tab
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: "#fff" }}>
        <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">AI Collections Predictions</p>
        <div className="space-y-3">
          {[
            { student: "Sarah Nakato", amount: "UGX 450K", risk: "Low", color: "#8A6520", prob: "95%" },
            { student: "John Mukasa", amount: "UGX 850K", risk: "Medium", color: "#E07B00", prob: "62%" },
            { student: "Grace Auma", amount: "UGX 1.2M", risk: "High", color: "#D32F2F", prob: "28%" },
            { student: "Peter Otieno", amount: "UGX 300K", risk: "Low", color: "#8A6520", prob: "88%" },
          ].map((r, i) => (
            <motion.div key={r.student} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} className="flex items-center justify-between rounded-xl border p-3" style={{ borderColor: "rgba(26,26,26,0.06)" }}>
              <div>
                <p className="text-sm font-medium text-[#1C1917]">{r.student}</p>
                <p className="text-xs text-[#888]">{r.amount} outstanding</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#888]">{r.prob}</span>
                <span className="rounded-full px-3 py-1 text-[10px] font-medium text-white" style={{ background: r.color }}>{r.risk}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border p-5" style={{ borderColor: `${product.color}15`, background: `${product.color}04` }}>
        <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#888]">AI Recommendations</p>
        <div className="space-y-3">
          <div className="rounded-xl bg-white p-4">
            <p className="text-sm font-medium text-[#1C1917]">Offer Payment Plan</p>
            <p className="mt-1 text-xs text-[#4A4A4A]">Grace Auma (28% collection probability) — offering a 3-installment plan increases probability to 74%.</p>
            <button className="mt-3 rounded-full px-4 py-1.5 text-xs font-medium text-white" style={{ background: product.color }}>Generate Plan</button>
          </div>
          <div className="rounded-xl bg-white p-4">
            <p className="text-sm font-medium text-[#1C1917]">Send Reminder</p>
            <p className="mt-1 text-xs text-[#4A4A4A]">John Mukasa (62%) — auto-send WhatsApp reminder with payment link. Expected response: 48 hours.</p>
            <button className="mt-3 rounded-full px-4 py-1.5 text-xs font-medium text-white" style={{ background: product.color }}>Send Reminder</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InteractiveDemo;
